import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listAssessmentTemplates,
  renameAssessmentTemplate,
  addAssessmentTemplateSection,
  setAssessmentTemplateStatus,
  deleteAssessmentTemplateSection,
  reorderAssessmentTemplateSections,
  type AssessmentTemplateDto,
} from '../../lib/assessmentTemplates';
import { listCompetencies, type CompetencyDto } from '../../lib/competencies';
import {
  FileText,
  Pencil,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  ArrowLeft,
  Layers,
  Power,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export const Route = createFileRoute('/kinerja/templates/$templateId')({
  component: AssessmentTemplatePage,
});

function AssessmentTemplatePage() {
  return (
    <ProtectedRoute minimumRole="hr">
      <AssessmentTemplateContent />
    </ProtectedRoute>
  );
}

const SECTION_TYPES = [
  { value: 'competency', label: 'Competency Rating' },
  { value: 'goal', label: 'Goal Achievement' },
  { value: 'behavior', label: 'Behavioral Indicators' },
  { value: 'open_text', label: 'Open Text Response' },
  { value: 'peer_review', label: 'Peer Review' },
];

const SECTION_TYPE_COLORS: Record<string, string> = {
  competency: 'bg-blue-100 text-blue-700',
  goal: 'bg-emerald-100 text-emerald-700',
  behavior: 'bg-purple-100 text-purple-700',
  open_text: 'bg-amber-100 text-amber-700',
  peer_review: 'bg-pink-100 text-pink-700',
};

type SectionRow = {
  id: string;
  title: string;
  type: string;
  orderIndex: number;
  competencyId?: string;
  competencyCode?: string;
};

function AssessmentTemplateContent() {
  const { templateId } = Route.useParams();
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  const [template, setTemplate] = useState<AssessmentTemplateDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Rename
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameLoading, setNameLoading] = useState(false);

  // Status toggle
  const [statusLoading, setStatusLoading] = useState(false);

  // Section builder
  const [sections, setSections] = useState<SectionRow[]>([]);
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionType, setNewSectionType] = useState('competency');
  const [newSectionCompetencyId, setNewSectionCompetencyId] = useState('');
  const [sectionSaving, setSectionSaving] = useState(false);
  const [sectionError, setSectionError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  // Competencies for picker
  const [competencies, setCompetencies] = useState<CompetencyDto[]>([]);

  useEffect(() => {
    let cancelled = false;
    listAssessmentTemplates(companyId, true).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) {
        const found = res.data.templates.find((t) => t.id === templateId);
        if (found) {
          setTemplate(found);
          setNameValue(found.name);
          setSections(
            found.sections.map((s) => ({
              id: s.id,
              title: s.title,
              type: (s.config as any)?.type ?? 'competency',
              orderIndex: s.orderIndex,
              competencyId: (s.config as any)?.competencyId,
              competencyCode: (s.config as any)?.competencyCode,
            })),
          );
        } else {
          setError('Template not found');
        }
      } else {
        setError('Failed to load templates');
      }
    });
    return () => { cancelled = true; };
  }, [templateId, companyId]);

  // Load competencies for the picker
  useEffect(() => {
    let cancelled = false;
    listCompetencies({ companyId }).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) setCompetencies(res.data.competencies);
    });
    return () => { cancelled = true; };
  }, [companyId]);

  async function handleRenameSave() {
    if (!template) return;
    setNameLoading(true);
    const res = await renameAssessmentTemplate({ templateId: template.id, companyId, name: nameValue });
    setNameLoading(false);
    if (res.success) {
      toast.success('Template renamed');
      setTemplate((prev) => (prev ? { ...prev, name: nameValue } : prev));
      setEditingName(false);
    } else {
      toast.error(res.error ?? 'Failed to rename template');
    }
  }

  async function handleToggleStatus() {
    if (!template) return;
    setStatusLoading(true);
    const res = await setAssessmentTemplateStatus({
      templateId: template.id,
      companyId,
      isActive: !template.isActive,
    });
    setStatusLoading(false);
    if (res.success) {
      toast.success(template.isActive ? 'Template deactivated' : 'Template activated');
      setTemplate((prev) => (prev ? { ...prev, isActive: !prev.isActive } : prev));
    } else {
      toast.error(res.error ?? 'Failed to update template status');
    }
  }

  async function handleAddSection() {
    if (!newSectionTitle.trim() || !template) return;
    setSectionSaving(true);
    setSectionError('');

    const config: Record<string, unknown> = { type: newSectionType };
    if (newSectionType === 'competency' && newSectionCompetencyId) {
      const comp = competencies.find((c) => c.id === newSectionCompetencyId);
      config.competencyId = newSectionCompetencyId;
      config.competencyCode = comp?.code;
    }

    const res = await addAssessmentTemplateSection(
      template.id,
      {
        companyId,
        title: newSectionTitle.trim(),
        orderIndex: sections.length,
        config,
      },
    );
    setSectionSaving(false);

    if (res.success && res.data) {
      toast.success('Section added');
      setSections((prev) => [
        ...prev,
        {
          id: res.data!.section.id,
          title: res.data!.section.title,
          type: (res.data!.section.config as any)?.type ?? newSectionType,
          orderIndex: res.data!.section.orderIndex,
          competencyId: (res.data!.section.config as any)?.competencyId,
          competencyCode: (res.data!.section.config as any)?.competencyCode,
        },
      ]);
      setNewSectionTitle('');
      setNewSectionType('competency');
      setNewSectionCompetencyId('');
      setAddingSection(false);
    } else {
      toast.error(res.error ?? 'Failed to add section');
      setSectionError(res.error ?? 'Failed to add section');
    }
  }

  async function removeSection(id: string) {
    if (!template) return;
    setDeletingId(id);
    const res = await deleteAssessmentTemplateSection(template.id, id, companyId);
    setDeletingId(null);
    if (res.success) {
      toast.success('Section deleted');
      setSections((prev) => prev.filter((s) => s.id !== id));
    } else {
      toast.error(res.error ?? 'Failed to delete section');
      setSectionError(res.error ?? 'Failed to delete section');
    }
  }

  async function moveSection(idx: number, dir: -1 | 1) {
    if (!template || reordering) return;
    const next = [...sections];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    const reordered = next.map((s, i) => ({ ...s, orderIndex: i }));
    // Optimistic update first, then persist
    setSections(reordered);
    setReordering(true);
    const res = await reorderAssessmentTemplateSections(
      template.id,
      reordered.map((s) => s.id),
      companyId,
    );
    setReordering(false);
    if (!res.success) {
      // Rollback to previous order on failure
      setSections(sections);
      toast.error(res.error ?? 'Failed to reorder sections');
      setSectionError(res.error ?? 'Failed to reorder sections');
    }
  }

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="h-8 w-64 bg-muted rounded animate-pulse" />
        <div className="h-48 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-muted-foreground text-sm">{error || 'Template not found'}</p>
        <Link to={'/kinerja/templates' as any}>
          <Button variant="link" className="mt-2">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Templates
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back link */}
      <Link to={'/kinerja/templates' as any}>
        <Button variant="ghost" size="sm" className="text-muted-foreground">
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Templates
        </Button>
      </Link>

      {/* Template header card */}
      <Card>
        <CardContent className="py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    className="flex-1"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameSave();
                      if (e.key === 'Escape') { setEditingName(false); setNameValue(template.name); }
                    }}
                  />
                  <Button size="sm" onClick={handleRenameSave} disabled={nameLoading}>
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditingName(false); setNameValue(template.name); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-primary" />
                    </div>
                    <h1 className="text-base font-semibold text-foreground">{template.name}</h1>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingName(true)}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              {template.description && (
                <p className="text-sm text-muted-foreground mt-1.5 ml-10">{template.description}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <Badge variant={template.isActive ? 'success' : 'secondary'}>
                {template.isActive ? 'Active' : 'Inactive'}
              </Badge>
              <span className="text-xs text-muted-foreground">v{template.version}</span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                onClick={handleToggleStatus}
                disabled={statusLoading}
              >
                <Power className="w-3 h-3" />
                {statusLoading ? 'Saving…' : template.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section builder card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">
              <span className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-primary" />
                Sections ({sections.length})
              </span>
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddingSection(true)}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Section
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-2">
          {sections.length === 0 && !addingSection ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No sections yet. Add the first section to define the assessment structure.
            </div>
          ) : (
            <div className="space-y-2">
              {sections.map((s, idx) => {
                const typeColorClass = SECTION_TYPE_COLORS[s.type] ?? 'bg-gray-100 text-gray-600';
                return (
                  <div
                    key={s.id}
                    className="flex items-center gap-3 bg-muted/50 rounded-lg px-3 py-2.5 group"
                  >
                    <span className="text-xs font-mono text-muted-foreground w-5 text-center shrink-0">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{s.title}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={cn('text-xs px-1.5 py-0.5 rounded-full font-medium', typeColorClass)}>
                          {SECTION_TYPES.find((t) => t.value === s.type)?.label ?? s.type}
                        </span>
                        {s.competencyCode && (
                          <span className="text-xs font-mono text-muted-foreground">
                            {s.competencyCode}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => moveSection(idx, -1)}
                        disabled={idx === 0 || reordering}
                        className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 rounded"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSection(idx, 1)}
                        disabled={idx === sections.length - 1 || reordering}
                        className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 rounded"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSection(s.id)}
                        disabled={deletingId === s.id}
                        className="p-1 text-muted-foreground hover:text-destructive rounded disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Inline add section form */}
          {addingSection && (
            <div className="mt-2 p-3 border rounded-lg bg-accent/40 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="sec-title">Section title</Label>
                <Input
                  id="sec-title"
                  autoFocus
                  placeholder="e.g. Core Competencies"
                  value={newSectionTitle}
                  onChange={(e) => setNewSectionTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setAddingSection(false);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sec-type">Section type</Label>
                <Select
                  value={newSectionType}
                  onValueChange={(v) => {
                    setNewSectionType(v);
                    if (v !== 'competency') setNewSectionCompetencyId('');
                  }}
                >
                  <SelectTrigger id="sec-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTION_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Competency picker — shown only for competency type */}
              {newSectionType === 'competency' && (
                <div className="space-y-1.5">
                  <Label htmlFor="sec-comp">Competency (optional)</Label>
                  <Select
                    value={newSectionCompetencyId || '__none__'}
                    onValueChange={(v) => setNewSectionCompetencyId(v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger id="sec-comp">
                      <SelectValue placeholder="All competencies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">All competencies</SelectItem>
                      {competencies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="font-mono text-xs mr-1.5">{c.code}</span>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Link to a specific competency so this section contributes to competency scoring.
                  </p>
                </div>
              )}

              {sectionError && <p className="text-xs text-destructive">{sectionError}</p>}

              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddSection} className="flex-1" disabled={sectionSaving}>
                  {sectionSaving ? 'Saving…' : 'Add'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setAddingSection(false); setSectionError(''); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
